import { shouldPolyfill as shouldPolyfillDateTimeFormat } from '@formatjs/intl-datetimeformat/should-polyfill';
import { shouldPolyfill as shouldPolyfillDisplayNames } from '@formatjs/intl-displaynames/should-polyfill';
import { shouldPolyfill as shouldPolyfillGetCanonicalLocales } from '@formatjs/intl-getcanonicallocales/should-polyfill';
import { shouldPolyfill as shouldPolyfillListFormat } from '@formatjs/intl-listformat/should-polyfill';
import { shouldPolyfill as shouldPolyfillLocale } from '@formatjs/intl-locale/should-polyfill';
import { shouldPolyfill as shouldPolyfillNumberFormat } from '@formatjs/intl-numberformat/should-polyfill';
import { shouldPolyfill as shouldPolyfillPluralRules } from '@formatjs/intl-pluralrules/should-polyfill';
import { shouldPolyfill as shouldPolyfillRelativeTimeFormat } from '@formatjs/intl-relativetimeformat/should-polyfill';
import areIntlLocalesSupported from 'intl-locales-supported';
import get from 'lodash/get';
import { all, call, put, select } from 'typed-redux-saga/macro';

import {
  I13N_ACTION_CHANGE_VALUE,
  I13N_CATEGORY_SAGA,
  I13N_CATEGORY_SETTINGS,
  I13N_TYPE_EXCEPTION,
  I13N_TYPE_FEATURE_COUNT,
} from 'Lib/constants/i13nSpec';
import getCookiesInstance from 'Lib/Cookies';
import { debugTranslations as debug } from 'Lib/debug';
import getI13nUtils from 'Lib/i13n/getI13nUtils';
import { GetActionByType } from 'Lib/reduxType';
import requestIdleCallbackManager from 'Lib/requestIdleCallbackManager';
import { withErrorHandling } from 'Lib/sagaHelpers';
import { postMessage } from 'Lib/serviceWorkerHelper';

import { loadIntlMessageSuccess } from 'Store/intlMessages/actions';
import { updateGlobalModePreferences } from 'Store/profile/actions';

import getLocaleObject from './utils/getLocaleObject';
import loadIntlMessage from './utils/loadIntlMessage';
import { loadLanguageSuccess } from './actions';
import { DEFAULT_LANGUAGE, LOAD_LANGUAGE } from './constants';
import {
  // methods
  loadIntlCanonicalLocalesPolyfill,
  loadIntlDateTimeFormatPolyfill,
  loadIntlDateTimeFormatTimezone,
  loadIntlDisplayNamesPolyfill,
  loadIntlListFormatPolyfill,
  loadIntlLocalePolyfill,
  loadIntlNumberFormatPolyfill,
  loadIntlPluralRulesPolyfill,
  // Intl base
  loadIntlPolyfill,
  loadIntlRelativeTimeFormatPolyfill,
} from './polyfills';
import { getCurrentLanguage } from './selectors';

const cookies = getCookiesInstance();
const { executeEvent } = getI13nUtils();

/**
 * Intl Polyfill Depedencies
 * - Intl method (intl package)
 * https://formatjs.io/docs/polyfills
 * - Intl.getCanonicalLocales
 * - Intl.Locale
 *   -> Intl.PluralRules
 *     -> Intl.NumberFormat (ES2020)
 *       -> Intl.RelativeTimeFormat
 *       -> Intl.DateTimeFormat
 *   -> Intl.DisplayNames
 */

const handlePolyfillError = (e, label) => {
  debug(e);
  executeEvent(I13N_TYPE_EXCEPTION, {
    category: I13N_CATEGORY_SAGA,
    action: I13N_ACTION_CHANGE_VALUE,
    label,
    message: e?.message,
  });
};

// Main function that handles browser polyfill
function* _handleIntlPolyfillCompatibility(localeObject: ReturnType<typeof getLocaleObject>) {
  const { fullLocale, shortLocale } = localeObject;
  debug('handleIntlPolyfillCompatibility', localeObject);

  // https://caniuse.com/#search=Intl
  if (!Intl) {
    yield* call(loadIntlPolyfill);
  }

  const {
    DateTimeFormat,
    DisplayNames,
    ListFormat,
    NumberFormat,
    PluralRules,
    RelativeTimeFormat,
  } = window.Intl;

  /**
   * Intl methods polyfills (order matters)
   */

  // IE 11-, required for all other polyfills
  if (shouldPolyfillGetCanonicalLocales()) {
    try {
      yield* call(loadIntlCanonicalLocalesPolyfill);
    } catch (e) {
      handlePolyfillError(e, 'getCanonicalLocales');
    }
  }

  if (shouldPolyfillLocale()) {
    try {
      yield* call(loadIntlLocalePolyfill);
    } catch (e) {
      handlePolyfillError(e, 'Locale');
    }
  }

  yield* all([
    // required for NumberFormat
    call(function* handlePluralRules() {
      if (
        !areIntlLocalesSupported([shortLocale], [PluralRules]) ||
        shouldPolyfillPluralRules(shortLocale)
      ) {
        try {
          if (!get(Intl, ['PluralRules', 'polyfilled'])) {
            yield* call(loadIntlPluralRulesPolyfill);
          }
          const { default: loadPluralRulesData } = yield import('./polyfillsData/intl-pluralrules');
          yield* call(loadPluralRulesData, shortLocale);
        } catch (e) {
          handlePolyfillError(e, 'PluralRules');
        }
      }
    }),
    call(function* handleDisplayNames() {
      if (
        !areIntlLocalesSupported([fullLocale], [DisplayNames]) ||
        shouldPolyfillDisplayNames(fullLocale)
      ) {
        try {
          if (!get(Intl, ['DisplayNames', 'polyfilled'])) {
            yield* call(loadIntlDisplayNamesPolyfill);
          }
          const { default: loadIntlDisplayNamesData } = yield import(
            './polyfillsData/intl-displaynames'
          );
          yield* call(loadIntlDisplayNamesData, fullLocale);
        } catch (e) {
          handlePolyfillError(e, 'DisplayNames');
        }
      }
    }),
    call(function* handleListFormat() {
      if (
        !areIntlLocalesSupported([shortLocale], [ListFormat]) ||
        shouldPolyfillListFormat(shortLocale)
      ) {
        try {
          if (!get(Intl, ['ListFormat', 'polyfilled'])) {
            yield* call(loadIntlListFormatPolyfill);
          }
          const { default: loadIntlListFormatData } = yield import(
            './polyfillsData/intl-listformat'
          );
          yield* call(loadIntlListFormatData, shortLocale);
        } catch (e) {
          handlePolyfillError(e, 'ListFormat');
        }
      }
    }),
  ]);

  // https://stackoverflow.com/a/60588156
  // Required for DateTimeFormat and RelativeTimeFormat
  if (
    !areIntlLocalesSupported([fullLocale], [NumberFormat]) ||
    shouldPolyfillNumberFormat(fullLocale)
  ) {
    try {
      if (!get(Intl, ['NumberFormat', 'polyfilled'])) {
        yield* call(loadIntlNumberFormatPolyfill);
      }
      const { default: loadIntlNumberFormatData } = yield import(
        './polyfillsData/intl-numberformat'
      );
      yield* call(loadIntlNumberFormatData, fullLocale);
    } catch (e) {
      handlePolyfillError(e, 'NumberFormat');
    }
  }

  yield* all([
    call(function* handleDateTimeFormat() {
      if (
        !areIntlLocalesSupported([fullLocale], [DateTimeFormat]) ||
        shouldPolyfillDateTimeFormat(fullLocale)
      ) {
        try {
          if (!get(Intl, ['DateTimeFormat', 'polyfilled'])) {
            yield* call(loadIntlDateTimeFormatPolyfill);
          }
          const { default: loadIntlDateTimeFormatData } = yield import(
            './polyfillsData/intl-datetimeformat'
          );
          yield* all([
            call(loadIntlDateTimeFormatTimezone),
            call(loadIntlDateTimeFormatData, fullLocale),
          ]);

          const timezone = Intl.DateTimeFormat()?.resolvedOptions()?.timeZone;
          if ('__setDefaultTimeZone' in Intl.DateTimeFormat && timezone) {
            // @ts-expect-error - this is from polyfill
            Intl.DateTimeFormat.__setDefaultTimeZone(timezone);
          }
        } catch (e) {
          handlePolyfillError(e, 'DateTimeFormat');
        }
      }
    }),
    call(function* handleRelativeTimeFormat() {
      if (
        !areIntlLocalesSupported([fullLocale], [RelativeTimeFormat]) ||
        shouldPolyfillRelativeTimeFormat(fullLocale)
      ) {
        try {
          if (!get(Intl, ['RelativeTimeFormat', 'polyfilled'])) {
            yield* call(loadIntlRelativeTimeFormatPolyfill);
          }
          const { default: loadRelativeTimeFormatData } = yield import(
            './polyfillsData/intl-relativetimeformat'
          );
          yield* call(loadRelativeTimeFormatData, shortLocale);
        } catch (e) {
          handlePolyfillError(e, 'RelativeTimeFormat');
        }
      }
    }),
  ]);
}

const handleIntlPolyfillCompatibility = withErrorHandling(_handleIntlPolyfillCompatibility, {
  name: 'handleIntlPolyfillCompatibility',
});
/**
 * Saga to load intl related assets
 * 1. check if browser support Intl, and load polyfill if needed
 * 2. load react-intl locale-data
 * 3. load intl message
 */
function* _loadLanguageSaga(action: GetActionByType<typeof LOAD_LANGUAGE>) {
  debug('load language saga start', action);
  const currentLang = yield* select(getCurrentLanguage);
  // Locale info
  const localeObject = getLocaleObject(action.payload);

  const { fullLocale, shortLocale } = localeObject;

  // 2. load Intl polyfill
  yield* call(handleIntlPolyfillCompatibility, localeObject);

  // 3. load translation message
  const shouldLoadIntlMessage = !new Set([shortLocale, fullLocale]).has(currentLang);
  let localeUsed = fullLocale;
  let messagesDataModule;

  if (shouldLoadIntlMessage) {
    debug('Start loading intl message', action.payload);
    try {
      debug('Try loading full locale', fullLocale);
      messagesDataModule = yield* call(loadIntlMessage, fullLocale);
    } catch (errorLoadingFullIntl) {
      try {
        debug('Try loading short locale', shortLocale);
        messagesDataModule = yield* call(loadIntlMessage, shortLocale);
        localeUsed = shortLocale;
      } catch (errorLoadingShortIntl) {
        debug('Fallback to', DEFAULT_LANGUAGE);
        messagesDataModule = yield* call(loadIntlMessage, DEFAULT_LANGUAGE);
        localeUsed = DEFAULT_LANGUAGE;
      }
    }

    executeEvent(I13N_TYPE_FEATURE_COUNT, {
      category: I13N_CATEGORY_SETTINGS,
      action: I13N_ACTION_CHANGE_VALUE,
      label: 'I18nLogic',
      message: `Lang change from ${currentLang} to ${localeUsed}`,
    });
    yield* put(loadIntlMessageSuccess(messagesDataModule));
  }

  debug('load language success', localeUsed);
  yield* put(loadLanguageSuccess(localeUsed));

  yield* put(updateGlobalModePreferences(new Set([localeUsed]), true));

  requestIdleCallbackManager.addTask(() => {
    if (currentLang !== localeUsed) {
      cookies.set('lang', localeUsed);
      postMessage('INVALIDATE_SERVER_RENDER', { locale: localeUsed });
    }
  });
}

export const loadLanguageSaga = withErrorHandling(_loadLanguageSaga, { name: 'loadLanguageSaga' });
